import { BaseModel } from '../base.model';
import { AGMediaKeys } from '../utils/ag-media.model';

export enum CarrierProductionFileId {
  TransamericaSubmitWeeklyFile = 'transamerica-submit-weekly-file',
  TransamericaPaidWeeklyFile = 'transamerica-paid-weekly-file',
  ForestersSubmitWeeklyFeed = 'foresters-submit-weekly-feed',
  ForestersPaidYtdFeed = 'foresters-paid-ytd-feed',
  NationalLifeSubmitWeeklyFeed = 'national-life-submit-weekly-feed',
  NationalLifePaidYtdFeed = 'national-life-paid-ytd-feed',
  AimcorSubmitWeeklyFeed = 'aimcor-submit-weekly-feed',
  AimcorPaidYtdFeed = 'aimcor-paid-ytd-feed',
  EquisValueTeachersSubmitWeeklyFeed = 'equis-value-teachers-submit-weekly-feed',
  EquisValueTeachersPaidYtdFeed = 'equis-value-teachers-paid-ytd-feed',
  AmeritasSubmitSheet = 'ameritas-submit-sheet',
  AmeritasPaidSheet = 'ameritas-paid-sheet',
  AmeritasLopesSheet = 'ameritas-lopes-sheet',
  GpmFile1 = 'gpm-file-1',
  GpmFile2 = 'gpm-file-2',
  GpmFile3 = 'gpm-file-3',
  GpmFile4 = 'gpm-file-4',
  CorebridgeWeeklyExcelFile = 'corebridge-weekly-excel-file',
  AnicoFile1 = 'anico-file-1',
  AnicoFile2 = 'anico-file-2',
  GslSubmitWeeklyFile = 'gsl-submit-weekly-file',
  GslPaidWeeklyFile = 'gsl-paid-weekly-file',
  FigMonthlyFile1 = 'fig-monthly-file-1',
  FigMonthlyFile2 = 'fig-monthly-file-2',
  FigMonthlyFile3 = 'fig-monthly-file-3',
}

export const CARRIER_PRODUCTION_FILE_IDS = Object.values(CarrierProductionFileId) as CarrierProductionFileId[];
const CARRIER_PRODUCTION_FILE_ID_SET = new Set<CarrierProductionFileId>(CARRIER_PRODUCTION_FILE_IDS);

export enum CarrierProductionManualReconciliationMetricKey {
  submitPremiumTotal = 'submitPremiumTotal',
  submitPolicyCount = 'submitPolicyCount',
  paidPremiumTotal = 'paidPremiumTotal',
  paidPolicyCount = 'paidPolicyCount',
}

export type CarrierProductionManualReconciliationMetrics = Partial<
  Record<CarrierProductionManualReconciliationMetricKey, number | null>
>;

/** Which measure of the reconciliation report a carrier's totals belong to. */
export enum CarrierProductionReconciliationSource {
  paid = 'paid',
  submit = 'submit',
}

/**
 * The period a carrier's total covers (TASKID-3644).
 *
 * A total means nothing without it: the same number is a week on one feed and a year to date on
 * the next, and comparing one against the other produces a variance of tens of times that reads
 * as a catastrophe. Declared per carrier rather than read off the file id -- `*PaidYtdFeed`
 * looks like it carries the answer, but a naming convention is not data and renaming an id
 * would move money.
 */
export enum CarrierProductionReconciliationBasis {
  week = 'week',
  ytd = 'ytd',
  month = 'month',
}

/**
 * A carrier's basis per measure. Partial because most feeds carry one: a submit feed declares
 * only `submit`. The sheets that carry both (Ameritas, Corebridge) declare both, and the source
 * itself needs no declaration -- the metric key already says which measure a total is.
 */
export type CarrierProductionReconciliationBasisBySource = Partial<
  Record<CarrierProductionReconciliationSource, CarrierProductionReconciliationBasis>
>;

const CARRIER_PRODUCTION_RECONCILIATION_SOURCE_BY_METRIC_KEY: Record<
  CarrierProductionManualReconciliationMetricKey,
  CarrierProductionReconciliationSource
> = {
  [CarrierProductionManualReconciliationMetricKey.submitPremiumTotal]: CarrierProductionReconciliationSource.submit,
  [CarrierProductionManualReconciliationMetricKey.submitPolicyCount]: CarrierProductionReconciliationSource.submit,
  [CarrierProductionManualReconciliationMetricKey.paidPremiumTotal]: CarrierProductionReconciliationSource.paid,
  [CarrierProductionManualReconciliationMetricKey.paidPolicyCount]: CarrierProductionReconciliationSource.paid,
};

export function getCarrierProductionReconciliationSource(
  metricKey: CarrierProductionManualReconciliationMetricKey,
): CarrierProductionReconciliationSource {
  return CARRIER_PRODUCTION_RECONCILIATION_SOURCE_BY_METRIC_KEY[metricKey];
}

const CARRIER_PRODUCTION_RECONCILIATION_PREMIUM_METRIC_KEY: Record<
  CarrierProductionReconciliationSource,
  CarrierProductionManualReconciliationMetricKey
> = {
  [CarrierProductionReconciliationSource.submit]: CarrierProductionManualReconciliationMetricKey.submitPremiumTotal,
  [CarrierProductionReconciliationSource.paid]: CarrierProductionManualReconciliationMetricKey.paidPremiumTotal,
};

/**
 * The money metric for a measure. Recorded figures are dollars only -- the policy counts the
 * carriers' totals also carry have nowhere to be stored and nothing to be compared against.
 */
export function getCarrierProductionReconciliationPremiumMetricKey(
  source: CarrierProductionReconciliationSource,
): CarrierProductionManualReconciliationMetricKey {
  return CARRIER_PRODUCTION_RECONCILIATION_PREMIUM_METRIC_KEY[source];
}

export enum CarrierProductionProcessingFlowMode {
  Legacy = 'legacy',
  Normalized = 'normalized',
  Both = 'both',
}

const CARRIER_PRODUCTION_PROCESSING_FLOW_MODE_SET = new Set<CarrierProductionProcessingFlowMode>(
  Object.values(CarrierProductionProcessingFlowMode),
);

export enum CarrierProductionRawCarrierFileProcessingOptionKey {
  ameritasUseLapse = 'ameritasUseLapse',
  ameritasHasLapse = 'ameritasHasLapse',
}

export type CarrierProductionRawCarrierFileProcessingOptions = Partial<
  Record<CarrierProductionRawCarrierFileProcessingOptionKey, boolean | null>
>;

export enum CarrierProductionRawCarrierFileKeys {
  carrierFileId = 'carrierFileId',
  reportDate = 'reportDate',
  uploadActionId = 'uploadActionId',
  processingStage = 'processingStage',
  processingStatus = 'processingStatus',
  legacyProcessingStatus = 'legacyProcessingStatus',
  normalizationProcessingStatus = 'normalizationProcessingStatus',
  processingHandler = 'processingHandler',
  processingFailureKind = 'processingFailureKind',
  processingErrorMessage = 'processingErrorMessage',
  processingStructureMismatch = 'processingStructureMismatch',
  lastHandledUploadActionId = 'lastHandledUploadActionId',
  queueRequestedAt = 'queueRequestedAt',
  queuedAt = 'queuedAt',
  processorStartedAt = 'processorStartedAt',
  processorCompletedAt = 'processorCompletedAt',
  stuckAt = 'stuckAt',
  recordsTotal = 'recordsTotal',
  recordsValid = 'recordsValid',
  recordsInvalid = 'recordsInvalid',
  manualReconciliationMetrics = 'manualReconciliationMetrics',
  processingFlow = 'processingFlow',
  processingOptions = 'processingOptions',
  finalFileName = 'finalFileName',
  finalFileWasabiPath = 'finalFileWasabiPath',
  finalFileContentType = 'finalFileContentType',
  submitImportReady = 'submitImportReady',
  submitImportRowsCount = 'submitImportRowsCount',
  submitImportUpdatedAt = 'submitImportUpdatedAt',
}

export enum CarrierProductionRawCarrierFileProcessingStatus {
  Uploaded = 'uploaded',
  Queued = 'queued',
  Processing = 'processing',
  NeedsUserInput = 'needs-user-input',
  Completed = 'completed',
  Failed = 'failed',
}

export enum CarrierProductionRawCarrierFileProcessingStage {
  QueueRequested = 'queue-requested',
  Queued = 'queued',
  ProcessorStarted = 'processor-started',
  DbRunCreateStarted = 'db-run-create-started',
  DbRunCreated = 'db-run-created',
  ProcessorCompleted = 'processor-completed',
  ProcessorFailed = 'processor-failed',
  Stuck = 'stuck',
}

export enum CarrierProductionRawCarrierFileFailureKind {
  StructureCheck = 'structure-check',
}

/**
 * One column whose header did not match the configured layout. `receivedHeader` is '' when the sheet
 * has no cell at that position at all (a column was dropped and everything after it shifted).
 */
export interface CarrierProductionColumnStructureMismatch {
  position: number;
  expectedHeader: string;
  receivedHeader: string;
  /** Alternate headers the configuration also accepts for this column, if any. */
  acceptedAliases?: string[];
}

/**
 * Detail behind a `StructureCheck` failure. The full expected/received layouts are carried alongside
 * the mismatches themselves: when a carrier drops a column, every position after it mismatches, and
 * the two side-by-side lists are what actually shows the operator that the file shifted rather than
 * that twenty columns were independently renamed.
 */
export interface CarrierProductionStructureMismatchDetails {
  sheetId: string;
  fileDescription: string;
  mismatches: CarrierProductionColumnStructureMismatch[];
  expectedColumns: { position: number; header: string }[];
  receivedColumns: { position: number; header: string }[];
}

export interface CarrierProductionRawCarrierFile extends BaseModel {
  [AGMediaKeys.fileName]: string;
  [AGMediaKeys.fileSize]: number;
  [AGMediaKeys.wasabiPath]: string;
  [AGMediaKeys.contentType]: string;
  [CarrierProductionRawCarrierFileKeys.carrierFileId]: CarrierProductionFileId;
  [CarrierProductionRawCarrierFileKeys.reportDate]: string;
  [CarrierProductionRawCarrierFileKeys.uploadActionId]: string;
  [CarrierProductionRawCarrierFileKeys.processingStage]?: CarrierProductionRawCarrierFileProcessingStage | null;
  [CarrierProductionRawCarrierFileKeys.processingStatus]: CarrierProductionRawCarrierFileProcessingStatus;
  [CarrierProductionRawCarrierFileKeys.legacyProcessingStatus]?: CarrierProductionRawCarrierFileProcessingStatus | null;
  [CarrierProductionRawCarrierFileKeys.normalizationProcessingStatus]?: CarrierProductionRawCarrierFileProcessingStatus | null;
  [CarrierProductionRawCarrierFileKeys.processingHandler]?: CarrierProductionFileId | null;
  [CarrierProductionRawCarrierFileKeys.processingFailureKind]?: CarrierProductionRawCarrierFileFailureKind | null;
  [CarrierProductionRawCarrierFileKeys.processingErrorMessage]?: string | null;
  [CarrierProductionRawCarrierFileKeys.processingStructureMismatch]?: CarrierProductionStructureMismatchDetails | null;
  [CarrierProductionRawCarrierFileKeys.lastHandledUploadActionId]?: string | null;
  [CarrierProductionRawCarrierFileKeys.queueRequestedAt]?: string | null;
  [CarrierProductionRawCarrierFileKeys.queuedAt]?: string | null;
  [CarrierProductionRawCarrierFileKeys.processorStartedAt]?: string | null;
  [CarrierProductionRawCarrierFileKeys.processorCompletedAt]?: string | null;
  [CarrierProductionRawCarrierFileKeys.stuckAt]?: string | null;
  [CarrierProductionRawCarrierFileKeys.recordsTotal]?: number | null;
  [CarrierProductionRawCarrierFileKeys.recordsValid]?: number | null;
  [CarrierProductionRawCarrierFileKeys.recordsInvalid]?: number | null;
  [CarrierProductionRawCarrierFileKeys.manualReconciliationMetrics]?: CarrierProductionManualReconciliationMetrics | null;
  [CarrierProductionRawCarrierFileKeys.processingFlow]?: CarrierProductionProcessingFlowMode | null;
  [CarrierProductionRawCarrierFileKeys.processingOptions]?: CarrierProductionRawCarrierFileProcessingOptions | null;
  [CarrierProductionRawCarrierFileKeys.finalFileName]?: string | null;
  [CarrierProductionRawCarrierFileKeys.finalFileWasabiPath]?: string | null;
  [CarrierProductionRawCarrierFileKeys.finalFileContentType]?: string | null;
  [CarrierProductionRawCarrierFileKeys.submitImportReady]?: boolean | null;
  [CarrierProductionRawCarrierFileKeys.submitImportRowsCount]?: number | null;
  [CarrierProductionRawCarrierFileKeys.submitImportUpdatedAt]?: string | null;
}

export function isCarrierProductionFileId(value: string): value is CarrierProductionFileId {
  return CARRIER_PRODUCTION_FILE_ID_SET.has(value as CarrierProductionFileId);
}

export function isCarrierProductionProcessingFlowMode(value: unknown): value is CarrierProductionProcessingFlowMode {
  return CARRIER_PRODUCTION_PROCESSING_FLOW_MODE_SET.has(value as CarrierProductionProcessingFlowMode);
}

export interface CarrierProductionFlowStageStatuses {
  legacyProcessingStatus: CarrierProductionRawCarrierFileProcessingStatus | null;
  normalizationProcessingStatus: CarrierProductionRawCarrierFileProcessingStatus | null;
}

export function resolveCarrierProductionFlowStageStatuses(args: {
  processingFlow: CarrierProductionProcessingFlowMode;
  processingStatus: CarrierProductionRawCarrierFileProcessingStatus;
  legacyProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
  normalizationProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
}): CarrierProductionFlowStageStatuses {
  if (args.processingFlow === CarrierProductionProcessingFlowMode.Legacy) {
    return {
      legacyProcessingStatus: args.legacyProcessingStatus ?? args.processingStatus,
      normalizationProcessingStatus: null,
    };
  }

  if (args.processingFlow === CarrierProductionProcessingFlowMode.Normalized) {
    return {
      legacyProcessingStatus: null,
      normalizationProcessingStatus: args.normalizationProcessingStatus ?? args.processingStatus,
    };
  }

  return {
    legacyProcessingStatus: args.legacyProcessingStatus ?? args.processingStatus,
    normalizationProcessingStatus: args.normalizationProcessingStatus ?? args.processingStatus,
  };
}

export function resolveCarrierProductionEffectiveProcessingStatus(args: {
  processingFlow?: CarrierProductionProcessingFlowMode | null;
  processingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
  legacyProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
  normalizationProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
}): CarrierProductionRawCarrierFileProcessingStatus | null {
  const statuses = resolveCandidateStatusesForEffectiveStatus(args);
  if (!statuses.length) {
    return args.processingStatus ?? null;
  }

  let winner = statuses[0];
  let winnerWeight = getCarrierProductionProcessingStatusWeight(winner);
  for (const status of statuses.slice(1)) {
    const weight = getCarrierProductionProcessingStatusWeight(status);
    if (weight > winnerWeight) {
      winner = status;
      winnerWeight = weight;
    }
  }

  return winner;
}

function resolveCandidateStatusesForEffectiveStatus(args: {
  processingFlow?: CarrierProductionProcessingFlowMode | null;
  processingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
  legacyProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
  normalizationProcessingStatus?: CarrierProductionRawCarrierFileProcessingStatus | null;
}): CarrierProductionRawCarrierFileProcessingStatus[] {
  const byFlow: CarrierProductionRawCarrierFileProcessingStatus[] = [];
  if (args.processingFlow === CarrierProductionProcessingFlowMode.Legacy) {
    if (args.legacyProcessingStatus) {
      byFlow.push(args.legacyProcessingStatus);
    }
    return byFlow;
  }

  if (args.processingFlow === CarrierProductionProcessingFlowMode.Normalized) {
    if (args.normalizationProcessingStatus) {
      byFlow.push(args.normalizationProcessingStatus);
    }
    return byFlow;
  }

  if (args.processingFlow === CarrierProductionProcessingFlowMode.Both) {
    if (args.legacyProcessingStatus) {
      byFlow.push(args.legacyProcessingStatus);
    }
    if (args.normalizationProcessingStatus) {
      byFlow.push(args.normalizationProcessingStatus);
    }
    return byFlow;
  }

  if (args.legacyProcessingStatus) {
    byFlow.push(args.legacyProcessingStatus);
  }
  if (args.normalizationProcessingStatus) {
    byFlow.push(args.normalizationProcessingStatus);
  }
  if (args.processingStatus) {
    byFlow.push(args.processingStatus);
  }
  return byFlow;
}

function getCarrierProductionProcessingStatusWeight(status: CarrierProductionRawCarrierFileProcessingStatus): number {
  if (status === CarrierProductionRawCarrierFileProcessingStatus.Failed) {
    return 60;
  }
  if (status === CarrierProductionRawCarrierFileProcessingStatus.NeedsUserInput) {
    return 50;
  }
  if (status === CarrierProductionRawCarrierFileProcessingStatus.Processing) {
    return 40;
  }
  if (status === CarrierProductionRawCarrierFileProcessingStatus.Queued) {
    return 30;
  }
  if (status === CarrierProductionRawCarrierFileProcessingStatus.Uploaded) {
    return 20;
  }
  if (status === CarrierProductionRawCarrierFileProcessingStatus.Completed) {
    return 10;
  }

  return 0;
}
